import React, { useState, useEffect } from 'react';
import API from '../api';
import { X, Mail, Send, Paperclip, AlertCircle } from 'lucide-react';

/**
 * InvioEmailModal: modal generico per inviare un documento (preventivo/SAL) via email.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   entity: 'preventivi' | 'sal' (route pattern: POST /api/{entity}/{id}/invia-email)
 *   docId: string
 *   defaultDestinatari?: string[]   → email pre-compilate (es. dal cliente della pratica)
 *   defaultOggetto?: string
 *   docLabel?: string               → es. "Preventivo PREV-2026-001"
 */
export default function InvioEmailModal({ open, onClose, entity, docId, defaultDestinatari = [], defaultOggetto = '', docLabel = 'documento' }) {
  const [destinatari, setDestinatari] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [includiPdf, setIncludiPdf] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setDestinatari((defaultDestinatari || []).join(', '));
      setOggetto(defaultOggetto || '');
      setMessaggio('');
      setResult(null);
      setErr('');
    }
  }, [open, defaultDestinatari, defaultOggetto]);

  if (!open) return null;

  const submit = async () => {
    const dest = destinatari.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    if (dest.length === 0) {
      setErr('Inserisci almeno un destinatario');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const { data } = await API.post(`/api/${entity}/${docId}/invia-email`, {
        destinatari: dest,
        oggetto: oggetto || undefined,
        messaggio: messaggio || undefined,
        includi_pdf: includiPdf,
      });
      setResult(data);
      setTimeout(() => onClose(), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Errore invio');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invio-email-modal">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Mail size={18} /> Invia {docLabel} via email</h3>
            <p className="text-xs text-slate-500 mt-0.5">L'email parte da onboarding@resend.dev. Il PDF sarà allegato automaticamente.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={20} /></button>
        </div>

        {result ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-3">
              <Send size={28} className="text-green-600" />
            </div>
            <h4 className="font-bold text-lg text-green-700">Email inviata!</h4>
            <p className="text-sm text-slate-600 mt-1">Destinatari: {(result.destinatari || []).join(', ')}</p>
            <p className="text-[10px] text-slate-400 mt-2">ID: {result.email_id}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide block mb-1">Destinatari (separati da virgola)</label>
                <input type="text" value={destinatari} onChange={e => setDestinatari(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40"
                  placeholder="cliente@email.it, altro@email.it"
                  data-testid="invio-email-destinatari" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide block mb-1">Oggetto</label>
                <input type="text" value={oggetto} onChange={e => setOggetto(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40"
                  placeholder="(se vuoto verrà generato automaticamente)"
                  data-testid="invio-email-oggetto" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide block mb-1">Messaggio personalizzato (opzionale)</label>
                <textarea rows={5} value={messaggio} onChange={e => setMessaggio(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary/40"
                  placeholder="Gentile Cliente, le invio in allegato il preventivo richiesto..."
                  data-testid="invio-email-messaggio" />
              </div>
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={includiPdf} onChange={e => setIncludiPdf(e.target.checked)}
                  data-testid="invio-email-include-pdf" />
                <span className="flex items-center gap-1"><Paperclip size={14} /> Allega PDF del {docLabel.toLowerCase()}</span>
              </label>
              {err && (
                <div className="bg-red-50 border border-red-200 rounded p-2.5 text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}
              <p className="text-[10px] text-slate-400 italic pt-1">
                ℹ️ In modalità sviluppo (Resend testing), le email possono essere consegnate solo a indirizzi verificati sull'account Resend di EdilCima. Per inviare a clienti esterni, è necessario verificare il dominio <code>edilcima.it</code> su Resend.
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded" data-testid="invio-email-cancel">Annulla</button>
              <button onClick={submit} disabled={busy}
                className="px-5 py-2 text-sm bg-primary text-white font-bold rounded hover:bg-primary disabled:opacity-50 flex items-center gap-1"
                data-testid="invio-email-submit">
                <Send size={14} /> {busy ? 'Invio…' : 'Invia email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
